import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import apiClient from "../lib/apiClient";
import PhotoMosaic from "../components/PhotoMosaic";

const useGalleryStyles = () => {
  useEffect(() => {
    const id = "mrr-gallery-page-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
      .ga-root {
        --ga-bg: #0c0f13; --ga-panel: #151a20; --ga-ink: #f5f1e8;
        --ga-muted: #a8b0ba; --ga-line: rgba(245,241,232,0.12); --ga-gold: #d6aa4b;
        background: var(--ga-bg); color: var(--ga-ink); min-height: 100vh;
        font-family: 'DM Sans', Arial, sans-serif;
      }
      .ga-shell { width: min(1180px, calc(100% - 32px)); margin: 0 auto; }
      .ga-hero { padding: 6rem 0 3rem; border-bottom: 1px solid var(--ga-line); }
      .ga-eyebrow { color: var(--ga-gold); font-size: 0.72rem; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; }
      .ga-title { font-family: 'Playfair Display', Georgia, serif; font-size: clamp(2.7rem, 7vw, 6.2rem); line-height: 0.98; margin: 0.8rem 0 1rem; }
      .ga-copy { color: var(--ga-muted); line-height: 1.75; max-width: 680px; }
      .ga-toolbar { display: flex; flex-wrap: wrap; gap: 0.6rem; padding: 2rem 0; }
      .ga-filter { border: 1px solid var(--ga-line); background: var(--ga-panel); color: var(--ga-ink); padding: 0.65rem 0.9rem; cursor: pointer; font-weight: 800; font-size: 0.78rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .ga-filter.active { background: var(--ga-gold); color: #111; border-color: var(--ga-gold); }
      .ga-empty { background: var(--ga-panel); border: 1px solid var(--ga-line); padding: 3rem; text-align: center; color: var(--ga-muted); margin-bottom: 5rem; }
    `;
    document.head.appendChild(style);
    return () => document.getElementById(id)?.remove();
  }, []);
};

const GalleryPage = () => {
  useGalleryStyles();
  const [photos, setPhotos] = useState([]);
  const [activeCategory, setActiveCategory] = useState("tout");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/api/gallery?placement=gallery")
      .then(res => setPhotos(res.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(photos.map(photo => photo.category).filter(Boolean)));
    return ["tout", ...unique];
  }, [photos]);

  const displayed = activeCategory === "tout"
    ? photos
    : photos.filter(photo => photo.category === activeCategory);

  return (
    <main className="ga-root">
      <Helmet>
        <title>Galerie | Mr. Renaudin Barbershop</title>
        <meta name="description" content="Decouvrez les coupes, finitions et inspirations publiees par Mr. Renaudin Barbershop a Shawinigan." />
      </Helmet>

      <section className="ga-hero">
        <div className="ga-shell">
          <p className="ga-eyebrow">Lookbook Mr. Renaudin</p>
          <h1 className="ga-title">Inspirez votre prochain style.</h1>
          <p className="ga-copy">
            Explorez les coupes, details de barbe, textures et finitions qui donnent au
            salon sa signature. Le lookbook presente des inspirations reelles pour choisir
            une coupe nette, actuelle et adaptee a votre style.
          </p>
        </div>
      </section>

      <section className="ga-shell">
        <div className="ga-toolbar" aria-label="Filtrer la galerie">
          {categories.map(category => (
            <button
              key={category}
              className={`ga-filter ${activeCategory === category ? "active" : ""}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="ga-empty">Chargement de la galerie...</div>
        ) : (
          <PhotoMosaic
            variant="gallery"
            photos={displayed}
            emptyTitle="La collection arrive bientot"
            emptyText="Les derniers styles du salon seront presentes ici avec les noms, categories et descriptions ajoutes depuis le dashboard."
          />
        )}
      </section>
    </main>
  );
};

export default GalleryPage;
